<template>
    <div class="domain-supergroups">
      <h4>Supergroups</h4>
      
      <!-- List of attached supergroups -->
      <div v-if="loading" class="text-center py-2">
        <div class="spinner-border spinner-border-sm text-primary">
          <span class="visually-hidden">Loading supergroups...</span>
        </div>
      </div>
      <div v-if="detachError" class="alert alert-danger py-1 px-2" role="alert">
        <i class="fa fa-exclamation-triangle me-1"></i>
        {{ detachError }}
      </div>
      <div v-else-if="error" class="alert alert-danger py-1 px-2" role="alert">
        <i class="fa fa-exclamation-triangle me-1"></i>
        {{ error }}
      </div>
      <div v-else>
        <div v-if="attachedSupergroups.length === 0" class="text-muted">
          No supergroups attached to this domain.
        </div>
        <ul v-else class="list-group mb-3">
          <li v-for="supergroup in attachedSupergroups" :key="supergroup.uuid" class="list-group-item d-flex justify-content-between align-items-center">
            {{ supergroup.name }}
            <span v-if="supergroup.type" class="badge bg-secondary me-2">{{ supergroup.type }}</span>
            <button @click="detachSupergroup(supergroup)" class="btn btn-sm btn-outline-danger" title="Detach" :disabled="!canManageSupergroups">
              <i class="fa fa-times"></i>
            </button>
          </li>
        </ul>
      </div>
      
      <!-- Button to open modal -->
      <button @click="openModal" class="btn btn-primary" :disabled="!canManageSupergroups">
        <i class="fa fa-plus me-1"></i> Attach Supergroup
      </button>
      
      <!-- Modal for attaching supergroups -->
      <div v-if="showModal" class="modal-backdrop">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <button type="button" class="close" @click="closeModal" aria-label="Close"><i class="fa fa-times"></i></button>
              <h5 class="modal-title">Attach Supergroup</h5>
            </div>
            <div class="modal-body">
              <div v-if="modalError" class="alert alert-danger" role="alert">
                {{ modalError }}
              </div>
              
              <!-- Supergroup search/autocomplete -->
              <div class="mb-3">
                <label for="supergroupSearch" class="form-label">Search Supergroups</label>
                <input
                  type="text"
                  class="form-control"
                  id="supergroupSearch"
                  v-model="searchQuery"
                  @input="searchSupergroups"
                  placeholder="Type to search..."
                  autocomplete="off"
                />
              </div>
              
              <!-- Search results -->
              <div v-if="searching" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-primary">
                  <span class="visually-hidden">Searching...</span>
                </div>
              </div>
              <div v-else-if="searchResults.length > 0" class="list-group">
                <button
                  v-for="result in searchResults"
                  :key="result.uuid"
                  class="list-group-item list-group-item-action"
                  @click="selectSupergroup(result)"
                  :disabled="isSupergroupAttached(result)"
                >
                  {{ result.name }}
                  <small v-if="result.hierarchy" class="text-muted">({{ result.hierarchy }})</small>
                  <span v-if="result.type" class="badge">{{ result.type }}</span>
                  <span v-if="isSupergroupAttached(result)" class="badge badge-info">Already attached</span>
                </button>
              </div>
              <div v-else-if="searchQuery && !searching" class="text-muted">
                No supergroups found matching your search.
              </div>

              <button type="button" class="btn btn-success" @click="attachSupergroup" :disabled="selectedSupergroup === null">Attach <span v-if="selectedSupergroup">to {{ selectedSupergroup.name }}</span></button>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { Permissions } from '@/client_auth/permissions'
  import { getSupergroupHierarchy } from '@/admin_entities/supergroups/supergroup_utils'
  export default {
    name: 'DomainSuperGroups',
    props: {
      domainUuid: {
        type: String,
        required: true
      }
    },
    computed: {
      loading() {
        return this.$angularWorld.domainInfo.domainInfoRecordUI.loading;
      },
      error() {
        return this.$angularWorld.domainInfo.domainInfoRecordUI.errorMessage;
      },
      domain() {
        return this.$angularWorld.domainInfo.domain;
      },
      attachedSupergroups() {
        return this.allSupergroups.filter(sg => this.$angularWorld.domainInfo?.domainInfoRecordUI?.domainInfoRecord?.supergroup_ids?.includes(sg.uuid));
      },
      supergroupMap() {
        return new Map(this.allSupergroups.map(sg => [sg.uuid, sg]));
      }
    },
    data() {
      return {
        allSupergroups: [],
        searchQuery: '',
        searchResults: [],
        searching: false,
        modalError: null,
        showModal: false,
        detachError: null,
        canManageSupergroups: false,
        selectedSupergroup: null,
      };
    },
    async mounted() {
      this.fetchAllSupergroups();
      await this.$angularWorld.auth.getCurrentUser()
      await this.$angularWorld.auth.doesUserHavePermission([Permissions.UserOps]).then(hasPermission => {
        this.canManageSupergroups = hasPermission;
      })
    },
    methods: {
      getSupergroupHierarchy(supergroup) {
        return getSupergroupHierarchy(supergroup, this.supergroupMap);
      },
      async fetchAllSupergroups() {
        try {
          const response = await this.$angularWorld.adminEntitiesApi.listSupergroups({});
          this.allSupergroups = response.data || [];
        } catch (error) {
          console.error('Error fetching all supergroups:', error);
        }
      },
  
      openModal() {
        this.searchQuery = '';
        this.searchResults = [];
        this.modalError = null;
        this.showModal = true;
      },
  
      closeModal() {
        this.showModal = false;
        this.searchQuery = '';
        this.searchResults = [];
        this.modalError = null;
        this.selectedSupergroup = null;
      },
  
      searchSupergroups() {
        if (!this.searchQuery.trim()) {
          this.searchResults = [];
          return;
        }
  
        this.searching = true;
  
        const query = this.searchQuery.toLowerCase();
        const results = this.allSupergroups.filter(sg =>
          sg.name.toLowerCase().includes(query) ||
          (sg.type && sg.type.toLowerCase().includes(query))
        );

        this.searchResults = results.map(sg => ({
          ...sg,
          hierarchy: this.getSupergroupHierarchy(sg)
        }));
  
        this.searching = false;
      },
  
      isSupergroupAttached(supergroup) {
        return this.attachedSupergroups.some(sg => sg.uuid === supergroup.uuid);
      },
  
      async selectSupergroup(supergroup) {
        if (this.isSupergroupAttached(supergroup)) {
          return;
        }
        this.selectedSupergroup = supergroup;
        this.searchResults = [];
        this.searchQuery = '';
      },

      async attachSupergroup() {
        try {  
          const existingIds = this.$angularWorld.domainInfo?.domainInfoRecordUI?.domainInfoRecord?.supergroup_ids || [];
          const supergroupIds = { supergroup_ids: [...existingIds, this.selectedSupergroup.uuid] };
          await this.$angularWorld.adminEntitiesApi.updateDomain(this.domainUuid, supergroupIds);
          this.$angularWorld.domainInfo.refreshDomainInfoRecord();
          this.showModal = false;
          this.selectedSupergroup = null;
        } catch (error) {
          console.error('Error attaching supergroup:', error);
          this.modalError = 'Failed to attach supergroup. Please try again.';
        }
      },

      async detachSupergroup(supergroup) {
        if (!confirm(`Are you sure you want to detach "${supergroup.name}" from this domain?`)) {
          return;
        }
  
        try {
          const supergroupIds = { supergroup_ids: (this.$angularWorld.domainInfo?.domainInfoRecordUI?.domainInfoRecord?.supergroup_ids || []).filter(id => id !== supergroup.uuid) };
          await this.$angularWorld.adminEntitiesApi.updateDomain(this.domainUuid, supergroupIds);
          this.$angularWorld.domainInfo.refreshDomainInfoRecord();
          this.selectedSupergroup = null;
        } catch (error) {
          console.error('Error detaching supergroup:', error);
          this.detachError = 'Failed to detach supergroup. Please try again.';
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .domain-supergroups {
    margin-bottom: 1rem;
  }
  
  .list-group-item {
    padding: 0.5rem 1rem;
  }
  
  .modal-body {
    max-height: 60vh;
    overflow-y: auto;
  }
  
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
  }
  
  .modal-dialog {
    position: relative;
    width: 500px;
    max-width: 90%;
    margin: 0 auto;
    z-index: 1051;
  }
  .btn-success {
    margin-top: 1rem;
  }
  </style>